import React, { useState, useLayoutEffect } from 'react';
import { validateFields } from './utils/validation';
import PageHeader from './components/PageHeader';
import MarketsSelector from './MarketsSelector';
import DialogAlert from './components/DialogAlert';
import DialogFooter from './components/DialogFooter';
import { DividerRow, FileField, SelectField, TextAreaField, TextField } from './components/FormFields';
import MarkdownEditorField from './components/MarkdownEditorField';
import { useAnimatedClose } from './hooks/useAnimatedClose';
import ProductPreviewCard from './ProductPreviewCard';
import FullScreenConfirmOverlay from './components/FullScreenConfirmOverlay';
import { showToastError, showToastSuccess, ToastInline } from './components/ToastNotifications';
import { getAllBrands } from './hooks/useBrands';
import { useImageUpload } from './hooks/useImageUpload';
import useEditProductMarkets from './hooks/useEditProductMarkets';
import {
  getProductCategories,
  getProductSubspecies,
  removeProduct,
  unarchiveProduct,
} from './hooks/useProducts';
import type { ProductRecord } from './types';
import { hasSelectedColoradoMarket } from './utils/productForm';

type EditProductDialogProps = {
  productId: number | string;
  product: ProductRecord;
  onClose: () => void;
  onSave: (product: Record<string, unknown>) => void;
  onAfterChange: () => void;
  organizationId: number | string;
};

export default function EditProductDialog({
  productId,
  product,
  onClose,
  onSave,
  onAfterChange,
  organizationId,
}: EditProductDialogProps) {
  const image = useImageUpload({ organizationId, kind: 'product' });
  const [form, setForm] = useState({
    productId: productId,
    name: '',
    brandId: '',
    markets: [],
    productCategoryName: '',
    productCategoryTypeId: '',
    strain: '',
    description: '',
    inventoryUpc: '',
    notes: '',
    active: false,
    subspeciesId: '',
    shelfLifeDays: '',
    showExpirationDateWhenAvailable: false,
    showUseByDateWhenAvailable: false,
    showSellByDateWhenAvailable: false,
  });

  const [errors, setErrors] = useState<Record<string, any>>({});
  const { close, isClosing } = useAnimatedClose(onClose);
  const [brandOptions, setBrandOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subspeciesOptions, setSubspeciesOptions] = useState([]);
  const [confirmArchiveOpen, setConfirmArchiveOpen] = useState(false);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const {
    markets,
    loading: marketsLoading,
    error: marketsError,
    isReady: marketsReady,
    retry: retryMarkets,
    selectMarkets,
  } = useEditProductMarkets({
    organizationId,
    productId,
    productIdentity: product,
    brandId: form.brandId,
    selectedMarketIds: product.marketIds || [],
  });

  useLayoutEffect(() => {
    let cancelled = false;
    const imageContext = image.reset();
    setBrandOptions([]);
    setCategoryOptions([]);
    setSubspeciesOptions([]);
    setErrors({});
    setForm({
      productId,
      name: '',
      brandId: '',
      markets: [],
      productCategoryName: '',
      productCategoryTypeId: '',
      strain: '',
      description: '',
      inventoryUpc: '',
      notes: '',
      active: false,
      subspeciesId: '',
      shelfLifeDays: '',
      showExpirationDateWhenAvailable: false,
      showUseByDateWhenAvailable: false,
      showSellByDateWhenAvailable: false,
    });

    if (!organizationId || !productId) {
      return;
    }

    async function loadProduct() {
      const [brandsResp, categoriesResp, subspeciesResp] = await Promise.all([
        getAllBrands(organizationId),
        getProductCategories({ organizationId }),
        getProductSubspecies({ organizationId }),
      ]);
      if (cancelled) return;

      const productResp = product;
      const foundBrand = brandsResp.find(b => b.id === productResp.brandId);
      const foundCategory = categoriesResp.find(pc => pc.id === productResp.productCategoryId);
      const foundSubspecies = subspeciesResp.find(ss => ss.id === productResp.subspeciesId);
      setBrandOptions(brandsResp);
      setCategoryOptions(categoriesResp);
      setSubspeciesOptions(subspeciesResp);
      image.hydrate(imageContext, {
        id: productResp.productImageId,
        name: productResp.productImageDisplayName,
        url: productResp.productImageUrl,
      });
      setForm({
        productId,
        name: productResp.name,
        brandId: foundBrand ? String(productResp.brandId) : '',
        markets: [],
        productCategoryName: foundCategory ? productResp.productCategoryName : '',
        productCategoryTypeId: foundCategory ? String(productResp.productCategoryId) : '',
        strain: productResp.strain || '',
        description: productResp.description || '',
        inventoryUpc: productResp.inventoryUpc || '',
        notes: productResp.notes || '',
        active: Boolean(productResp.active),
        subspeciesId: foundSubspecies ? String(productResp.subspeciesId) : '',
        shelfLifeDays: productResp.shelfLifeDays == null ? '' : String(productResp.shelfLifeDays),
        showExpirationDateWhenAvailable: Boolean(productResp.showExpirationDateWhenAvailable),
        showUseByDateWhenAvailable: Boolean(productResp.showUseByDateWhenAvailable),
        showSellByDateWhenAvailable: Boolean(productResp.showSellByDateWhenAvailable),
      });
    }

    loadProduct();

    return () => {
      cancelled = true;
    };
  }, [organizationId, productId, product]);

  const schema = [
    { name: 'name', required: true, message: 'Product name is required' },
    { name: 'brandId', required: true, message: 'Brand is required' },
    { name: 'productCategoryTypeId', required: true, message: 'Category is required' },
    {
      name: 'markets',
      validate: (markets) => {
        return !markets.some(m => m.selected);
      },
      message: 'Select at least one market'
    },
  ];

  const update = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
  }

  useLayoutEffect(() => {
    update('markets', markets);
  }, [markets]);

  const handleSave = () => {
    if (image.isPending) return;
    const { errors: nextErrors, isValid } = validateFields(schema, form);
    setErrors(nextErrors);
    if (!isValid) return;
    // remove empty strings in favor of null and ensure types for numbers
    const brandId = Number(form.brandId);
    const productCategoryTypeId = Number(form.productCategoryTypeId);
    const subspeciesId = form.subspeciesId === '' || form.subspeciesId === null ? null : Number(form.subspeciesId);
    const productImageId = image.id === '' || image.id === null ? null : Number(image.id);

    onSave({
      ...form,
      // override form values
      brandId: brandId,
      productCategoryTypeId: productCategoryTypeId,
      subspeciesId: Number.isNaN(subspeciesId as number) ? null : subspeciesId,
      productImageId: Number.isNaN(productImageId as number) ? null : productImageId,
      productImageUrl: image.id ? image.url : null,
      imageName: image.name,
      shelfLifeDays: form.shelfLifeDays === '' ? null : Number(form.shelfLifeDays),
      marketIds: form.markets.filter(m => m.selected).map(m => m.id)
    });
  };


  const archive = () => {
    setConfirmArchiveOpen(true);
  }

  const confirmArchive = async () => {
    if (archiveBusy) return;
    setArchiveBusy(true);
    try {
      await removeProduct(productId);
      showToastSuccess('Product archived');
      onAfterChange?.();
      close();
    } catch (e) {
      console.error(e);
      showToastError('Failed to archive product');
    } finally {
      setArchiveBusy(false);
      setConfirmArchiveOpen(false);
    }
  };

  const unArchive = async () => {
    if (archiveBusy) return;
    setArchiveBusy(true);
    try {
      await unarchiveProduct(productId);
      showToastSuccess('Product unarchived');
      onAfterChange?.();
      close();
    } catch (e) {
      console.error(e);
      showToastError('Failed to unarchive product');
    } finally {
      setArchiveBusy(false);
    }
  }


  return (
    <div className={`stack edit-product-page page-slide-in${isClosing ? ' page-slide-out' : ''}`} data-testid="edit-product">
      <FullScreenConfirmOverlay
        show={confirmArchiveOpen}
        title="Archive Product"
        message={`Are you sure you want to archive ${form.name || 'this product'}? This hides it from active listings.`}
        onConfirm={confirmArchive}
        onCancel={() => {
          if (archiveBusy) return;
          setConfirmArchiveOpen(false);
        }}
        confirmLabel={archiveBusy ? 'Archiving…' : 'Archive'}
        cancelLabel="Cancel"
        busy={archiveBusy}
        testId="confirm-archive-product-edit"
      />
      <PageHeader
        title="Edit Product"
        subtitle="Products"
        actions={(
          <button className="button secondary" onClick={close} data-testid="edit-product-close">Back</button>
        )}
      />

      <div className="drawer-body preview-right">
        <div className="drawer-preview">
          <div data-testid="edit-product-preview">
            <ProductPreviewCard
              previewEnabled={true}
              id={form.productId}
              name={form.name}
              brand={form.brandId ? brandOptions.find((b) => String(b.id) === String(form.brandId))?.displayName || '' : ''}
              category={form.productCategoryName}
              strain={form.strain}
              inventoryUpc={form.inventoryUpc}
              imageName={image.name}
              imageUrl={image.url}
              markets={form.markets.filter(m => m.selected).map(m => m.name || m.label || m.abbreviation || String(m.id))}
              description={form.description}

              isArchived={!form.active}
              archivedAt={null}
              showStatusBadge
              archiveHelp={''}
              emptyNameLabel="—"
              container="card"
            />
          </div>
        </div>

        <div className="drawer-controls">
          <div className="drawer-controls-card">
            <DialogAlert show={Object.keys(errors).length > 0} testId="edit-product-validation-error" />

            <div className="form-grid">
              <TextField
                label="Name"
                required
                value={form.name}
                onChange={(e) => update('name', e.target.value)}
                placeholder="Product name"
                error={errors.name}
                testId="edit-product-name"
                errorTestId="edit-product-name-error"
              />

              <SelectField
                label="Brand"
                required
                value={form.brandId}
                onChange={(e) => update('brandId', e.target.value)}
                error={errors.brandId}
                testId="edit-product-brand"
                errorTestId="edit-product-brand-error"
              >
                <option value="" disabled>Select a brand</option>
                {brandOptions
                  .slice()
                  .sort((a, b) => (a.name || '').localeCompare((b.name || ''), undefined, { sensitivity: 'base' }))
                  .map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </SelectField>

              <SelectField
                label="Category"
                required
                value={form.productCategoryTypeId}
                onChange={(e) => update('productCategoryTypeId', e.target.value)}
                error={errors.productCategoryTypeId}
                testId="edit-product-category"
                errorTestId="edit-product-category-error"
              >
                <option value="" disabled>Select a category</option>
                {categoryOptions
                  .slice()
                  .sort((a, b) => (a.name || '').localeCompare((b.name || ''), undefined, { sensitivity: 'base' }))
                  .map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </SelectField>

              <SelectField
                label="subspecies"
                value={form.subspeciesId}
                onChange={(e) => update('subspeciesId', e.target.value)}
                error={errors.subspeciesId}
                testId="edit-product-subspecies"
                errorTestId="edit-product-subspecies-error"
              >
                <option value="" disabled>Select a subspecie</option>
                {subspeciesOptions
                  .slice()
                  .sort((a, b) => (a.name || '').localeCompare((b.name || ''), undefined, { sensitivity: 'base' }))
                  .map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </SelectField>

              <TextField
                label="Strain"
                value={form.strain}
                onChange={(e) => update('strain', e.target.value)}
                placeholder="Optional strain name"
                testId="edit-product-strain"
              />

              <TextField
                label="Inventory UPC"
                value={form.inventoryUpc}
                onChange={(e) => update('inventoryUpc', e.target.value)}
                placeholder="Optional"
                testId="edit-product-inventory-upc"
              />

              <TextField
                label="Shelf life (calendar days)"
                type="number"
                min="0"
                step="1"
                value={form.shelfLifeDays}
                onChange={(e) => {
                  if (e.target.value === '' || /^\d+$/.test(e.target.value)) update('shelfLifeDays', e.target.value);
                }}
                placeholder="Optional"
                testId="edit-product-shelf-life-days"
              />

              <MarkdownEditorField
                label="Description"
                value={form.description}
                onChange={(next) => update('description', next)}
                placeholder="Short description (Markdown supported)"
                testId="edit-product-description"
                wrapperClassName="field full-span"
              />

              <ToastInline message={image.error} onDismiss={image.dismissError} />
              <FileField
                key={image.fileInputKey}
                label="Product Image"
                accept=".jpg, .jpeg, .png, .gif"
                onChange={(e) => image.select(e.target.files?.[0])}
                selectedName={image.name}
                helpText={"Max size: 5 MB | Supported file types: .jpg, .png, and .gif"}
                removeAction={image.remove}
                testId="edit-product-image"
              />

              <TextAreaField
                label="Notes (Internal)"
                rows={3}
                value={form.notes}
                onChange={(e) => update('notes', e.target.value)}
                placeholder="Internal only"
                testId="edit-product-notes"
              />

              {marketsReady && !hasSelectedColoradoMarket(form.markets) && <div className="field full-span">
                <label className="label">Consumer Date Display</label>
                {[
                  ['showExpirationDateWhenAvailable', 'Show Expiration Date When Available'],
                  ['showUseByDateWhenAvailable', 'Show Use By Date When Available'],
                  ['showSellByDateWhenAvailable', 'Show Sell By Date When Available'],
                ].map(([key, label]) => <label key={key} style={{ display: 'block', marginTop: 8 }} data-testid={`edit-product-${key}`}>
                  <input type="checkbox" checked={form[key]} onChange={(e) => update(key, e.target.checked)} data-testid={`edit-product-${key}-enabled`} /> {label}
                </label>)}
              </div>}

              <DividerRow />

              <div className="field full-span">
                {marketsError && (
                  <div className="field-error" role="alert" data-testid="edit-product-markets-error">
                    {marketsError}{' '}
                    <button
                      type="button"
                      className="button secondary"
                      onClick={retryMarkets}
                      data-testid="edit-product-markets-retry"
                    >
                      Retry
                    </button>
                  </div>
                )}
                <MarketsSelector
                  options={form.markets}
                  onChange={(next) => {
                    selectMarkets(next);
                    update('markets', next);
                  }}
                  error={errors.markets}
                  required
                  testId="edit-product-markets"
                />
              </div>
            </div>

            <DialogFooter>
              {form.active && (
                <button
                  className="button secondary"
                  onClick={archive}
                  data-testid="edit-product-archive"
                  disabled={archiveBusy}
                >
                  Archive Product
                </button>
              )}
              {!form.active && (
                <button
                  className="button secondary"
                  onClick={unArchive}
                  title={'Unarchive product'}
                  data-testid="edit-product-unarchive"
                  disabled={archiveBusy}
                >
                  Unarchive
                </button>
              )}
              <button className="button secondary" onClick={close} data-testid="edit-product-cancel">Cancel</button>
<button className="button" onClick={handleSave} data-testid="edit-product-save" disabled={marketsLoading || !marketsReady || image.isPending}>Save Changes</button>
            </DialogFooter>
          </div>
        </div>
      </div>
    </div>
  );
}
